export const CONTENT_PLATFORMS = ["bilibili", "xiaohongshu", "weibo", "douyin"] as const;
export type ContentPlatform = (typeof CONTENT_PLATFORMS)[number];

export const CONTENT_PLATFORM_META: Record<ContentPlatform, { label: string; color: string }> = {
  bilibili: { label: "B站", color: "#00aeec" },
  xiaohongshu: { label: "小红书", color: "#ff2442" },
  weibo: { label: "微博", color: "#ff8200" },
  douyin: { label: "抖音", color: "#161823" },
};

export interface ContentPlatformOutput {
  platform: ContentPlatform;
  title: string | null;
  body: string;
  hashtags: string[];
  publishTip: string;
  qualityWarnings: string[];
}

export type ContentOutputSet = Record<ContentPlatform, ContentPlatformOutput>;

export interface ContentRevision {
  id: string;
  sequence: number;
  operation: "initial_generate" | "group_regenerate" | "platform_rewrite" | "manual_edit" | "restore";
  targetPlatform: ContentPlatform | null;
  status: "running" | "succeeded" | "failed";
  outputs: ContentOutputSet | null;
  instruction: string | null;
  brainRunId: string | null;
  costCents: number | null;
  latencyMs: number | null;
  errorCode: string | null;
  errorMessage: string | null;
  createdBy: string;
  createdAt: string;
  completedAt: string | null;
}

export interface ContentDraftDetail {
  id: string;
  characterId: string;
  characterName: string;
  characterVersionId: string;
  characterVersion: number;
  brief: string;
  contextSnapshot: {
    plan: { id: string; dateString: string; title: string | null; plan: string; planSource: string } | null;
    moments: Array<{
      id: string;
      title: string | null;
      narrative: string;
      factSummary: string;
      startsAt: string;
      sourceType: string;
    }>;
    song?: {
      id: string;
      title: string;
      aliases: string[];
      originalArtists: string[];
      lyricists: string[];
      composers: string[];
      tags: string[];
      lyrics: string | null;
      recordings: Array<{
        versionName: string | null;
        kind: string;
        audioMode: string;
        occurredOn: string | null;
        staff: string | null;
        status: string;
        rightsStatus: string;
        publishedAt: string | null;
      }>;
    } | null;
  };
  status: "draft" | "finalized";
  currentRevision: ContentRevision | null;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ContentContextOptions {
  today: string;
  plans: Array<{
    id: string;
    dateString: string;
    title: string | null;
    plan: string;
    planSource: "manual" | "edited";
  }>;
  moments: Array<{
    id: string;
    title: string | null;
    narrative: string;
    factSummary: string;
    startsAt: string;
    sourceType: string;
  }>;
  songs: Array<{
    id: string;
    title: string;
    originalArtists: string[];
    hasLyrics: boolean;
    lyricsLength: number;
    recordings: Array<{ versionName: string | null; status: string }>;
  }>;
}

export function formatPublishPackage(output: ContentPlatformOutput): string {
  const lines: string[] = [];
  if (output.title) lines.push(output.title);
  if (output.body) lines.push(output.body);
  if (output.hashtags.length) lines.push(output.hashtags.map((tag) => `#${tag}`).join(" "));
  return lines.join("\n\n");
}

export function parseHashtags(value: string): string[] {
  return [...new Set(value.split(/[#,，、\n]+/).map((item) => item.trim()).filter(Boolean))].slice(0, 20);
}
